'use client';

import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onReadChapter?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setTextVisible(true), 400);
    return () => clearTimeout(t1);
  }, []);

  return (
    <header
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-obsidian pt-24 pb-16"
    >
      {/* Ambient glows */}
      <div className="ambient-glow w-[700px] h-[700px] bg-gold/6 top-[-150px] left-[-200px]" />
      <div className="ambient-glow w-[500px] h-[500px] bg-gold/4 bottom-[-100px] right-[-150px]" />

      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" aria-hidden="true">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-parchment"
            style={{
              width: `${1 + (i % 3)}px`,
              height: `${1 + (i % 3)}px`,
              left: `${(i * 5.2) % 100}%`,
              top: `${(i * 7.3) % 100}%`,
              opacity: 0.4,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left — Text Content */}
          <div className="order-2 lg:order-1">
            {/* Credential badge */}
            <div
              className={`inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-gold/25 bg-gold/6 text-xs font-body font-semibold text-gold tracking-widest uppercase mb-8 transition-all duration-700 ${
                textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
              CPRW Certified · Multi-Industry Executive Strategist
            </div>

            {/* Main headline */}
            <h1
              className={`font-display font-light text-parchment leading-[0.92] tracking-tight mb-6 transition-all duration-1000 delay-75 ${
                textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ fontSize: 'clamp(2.8rem, 5.5vw, 5.5rem)' }}
            >
              Stop Presenting<br />
              Your Job History.<br />
              <span className="italic font-light text-gold-gradient">Start Presenting</span><br />
              Your Business Case.
            </h1>

            {/* Subheading */}
            <p
              className={`font-body font-light text-parchment/60 text-lg leading-relaxed mb-10 max-w-lg transition-all duration-1000 delay-150 ${
                textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              Your next career leap isn't held back by a lack of experience.{' '}
              <strong className="font-semibold text-gold">It's often held back by how difficult it is for decision-makers to see, trust, and advocate for your value.</strong>{' '}
              I position your experience so hiring executives and board members can quickly feel confident about choosing you, reducing the doubt that keeps strong candidates from moving forward.
            </p>

            {/* CTAs */}
            <div
              className={`flex flex-col sm:flex-row items-start gap-4 mb-12 transition-all duration-1000 delay-225 ${
                textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <a
                href="mailto:jc.careerwriter@gmail.com"
                className="group gold-border-card px-8 py-4 rounded-full text-base font-body font-semibold text-gold hover:text-obsidian hover:bg-gold transition-all duration-400 flex items-center gap-3"
              >
                Email Judith Directly
                <Icon name="EnvelopeIcon" size={18} className="group-hover:scale-110 transition-transform" />
              </a>
              <a
                href="#services"
                className="px-8 py-4 rounded-full text-base font-body font-medium text-parchment/50 hover:text-parchment border border-parchment/10 hover:border-parchment/30 transition-all duration-300"
              >
                View Services
              </a>
            </div>

            {/* Stats */}
            <div
              className={`flex items-center gap-8 transition-all duration-1000 delay-300 ${
                textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {[
                { value: '75%+', label: 'ATS Filter Rate' },
                { value: '3', label: 'Service Tiers' },
                { value: '100%', label: 'ATS Compliant' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-display font-light text-2xl text-gold mb-0.5">{stat.value}</p>
                  <p className="text-parchment/35 text-xs font-body uppercase tracking-widest">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Photo */}
          <div
            className={`order-1 lg:order-2 flex justify-center lg:justify-end transition-all duration-1000 delay-100 ${
              textVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative">
              {/* Decorative ring */}
              <div className="absolute -inset-4 rounded-3xl border border-gold/10" />
              <div className="absolute -inset-8 rounded-3xl border border-gold/5" />

              {/* Photo */}
              <div className="relative w-[340px] md:w-[420px] rounded-2xl overflow-hidden shadow-obsidian">
                <AppImage
                  src="/assets/images/e8a7a7ca283848ff8b809b4ecc367ef4-1787068022604.jpg"
                  alt="Judith Carey, CPRW — Executive Resume Writer and LinkedIn Profile Expert"
                  className="w-full h-auto object-cover"
                  width={420}
                  height={560}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />

                {/* Name overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-display italic text-parchment text-2xl mb-1">Judith Carey, CPRW</p>
                  <p className="text-gold/80 text-xs font-body font-semibold tracking-widest uppercase">Executive Resume Writer & LinkedIn Expert</p>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-obsidian-card border border-gold/25 rounded-xl px-4 py-3 shadow-obsidian">
                <p className="text-gold text-xs font-body font-semibold tracking-widest uppercase">Principal Career</p>
                <p className="text-parchment text-xs font-body">Strategist</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-parchment/25 animate-float">
        <span className="text-xs font-body tracking-widest uppercase">Scroll</span>
        <Icon name="ChevronDownIcon" size={16} />
      </div>
    </header>
  );
};

export default HeroSection;